/**
 * <photo-gallery> — server-rendered photo grid with client-side load-more
 * and filtering.
 *
 * Items past the initial per-page count are server-rendered with [hidden].
 * The load-more button reveals the next batch and hides itself when exhausted.
 * Filter controls ([data-filter="<key>"]) match against data-photo-<key> on
 * each item — when any filter is active, all matches are shown and load-more
 * is hidden; clearing all filters restores the default per-page view.
 * Clicking any item opens the shared lightbox dialog referenced by
 * data-target-selector.
 */

import { populateLightbox, pauseLightboxMedia } from '@theme/photo-lightbox';

class PhotoGallery extends HTMLElement {
  #perPage = 24;
  #grid = null;
  #loadMoreButton = null;
  #emptyEl = null;
  #filterControls = [];
  #visibleCount = 0;

  connectedCallback() {
    this.#perPage = parseInt(this.dataset.perPage, 10) || 24;
    this.#grid = this.querySelector('[data-gallery-grid]');
    this.#loadMoreButton = this.querySelector('[data-load-more]');
    this.#emptyEl = this.querySelector('[data-gallery-empty]');
    this.#filterControls = Array.from(this.querySelectorAll('[data-filter]'));

    if (!this.#grid) return;

    const items = this.#items;
    this.#visibleCount = items.filter((item) => !item.hasAttribute('hidden')).length;

    this.#syncLoadMore();

    this.#loadMoreButton?.addEventListener('click', () => this.#revealNextBatch());

    for (const control of this.#filterControls) {
      control.addEventListener('change', () => this.#applyFilters());
    }

    this.#grid.addEventListener('click', (e) => {
      const button = e.target.closest('[data-gallery-item] button');
      if (!button) return;
      const item = button.closest('[data-gallery-item]');
      if (!item) return;
      this.#openLightbox(item);
    });
  }

  get #items() {
    return Array.from(this.#grid.querySelectorAll('[data-gallery-item]'));
  }

  #revealNextBatch() {
    const items = this.#items;
    const nextLimit = Math.min(this.#visibleCount + this.#perPage, items.length);
    let revealed = 0;
    for (let i = this.#visibleCount; i < nextLimit; i++) {
      const item = items[i];
      if (!item) break;
      item.removeAttribute('hidden');
      if (revealed === 0) {
        const btn = item.querySelector('button');
        if (btn) requestAnimationFrame(() => btn.focus({ preventScroll: true }));
      }
      revealed++;
    }
    this.#visibleCount = nextLimit;
    this.#syncLoadMore();
  }

  #syncLoadMore() {
    if (!this.#loadMoreButton) return;
    const total = this.#items.length;
    if (this.#visibleCount < total) {
      this.#loadMoreButton.hidden = false;
    } else {
      this.#loadMoreButton.hidden = true;
    }
  }

  #applyFilters() {
    const filters = this.#filterControls
      .map((c) => [c.dataset.filter, c.value || ''])
      .filter(([, value]) => value);

    if (filters.length === 0) {
      // Reset to default: first per-page items visible, rest hidden, load-more
      // restored if there are more items than the page can show.
      const items = this.#items;
      items.forEach((item, i) => {
        if (i < this.#perPage) item.removeAttribute('hidden');
        else item.setAttribute('hidden', '');
      });
      this.#visibleCount = Math.min(this.#perPage, items.length);
      if (this.#emptyEl) this.#emptyEl.hidden = true;
      this.#syncLoadMore();
      return;
    }

    let matched = 0;
    for (const item of this.#items) {
      const isMatch = filters.every(([key, value]) => {
        const dataKey = `photo${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        // Treat the data attribute as a pipe-delimited list. Single-value
        // attributes (country) split into one element, so equality matching
        // is just a list of length 1; multi-value (product) splits to many.
        const haystack = (item.dataset[dataKey] || '').split('|').filter(Boolean);
        return haystack.includes(value);
      });
      if (isMatch) {
        item.removeAttribute('hidden');
        matched++;
      } else {
        item.setAttribute('hidden', '');
      }
    }
    this.#visibleCount = matched;
    if (this.#loadMoreButton) this.#loadMoreButton.hidden = true;
    if (this.#emptyEl) this.#emptyEl.hidden = matched > 0;
  }

  #openLightbox(item) {
    const targetSelector = this.dataset.targetSelector;
    if (!targetSelector) {
      console.warn('[photo-gallery] no target-selector set');
      return;
    }
    const dialog = document.querySelector(targetSelector);
    if (!dialog) {
      console.warn('[photo-gallery] dialog not found for', targetSelector);
      return;
    }
    const host = dialog.closest('dialog-component');
    if (!host) {
      console.warn('[photo-gallery] dialog-component host missing');
      return;
    }

    // Pass every currently-visible item (respecting filters + load-more) as
    // the lightbox group so prev/next arrows walk the gallery in display
    // order, starting from the clicked item.
    const visibleItems = this.#items.filter((i) => !i.hasAttribute('hidden'));
    const photos = visibleItems.map((i) => this.#itemToPhoto(i));
    const startIndex = Math.max(0, visibleItems.indexOf(item));

    populateLightbox(dialog, photos, startIndex);

    host.addEventListener('dialog:close', () => pauseLightboxMedia(dialog), { once: true });

    if (typeof host.showDialog === 'function') host.showDialog();
  }

  #itemToPhoto(item) {
    const { dataset } = item;
    let products;
    return {
      full: dataset.photoFull,
      fullSrcset: dataset.photoFullSrcset,
      video: dataset.photoVideo,
      name: dataset.photoName,
      location: dataset.photoLocation,
      caption: dataset.photoCaption,
      date: dataset.photoDate,
      // Parse the products JSON lazily and memoize it: only the photo
      // actually rendered in the lightbox pays the cost, not every item in
      // the nav group that #openLightbox maps up front.
      get products() {
        if (products === undefined) {
          try { products = dataset.photoProducts ? JSON.parse(dataset.photoProducts) : []; }
          catch (_) { products = []; /* malformed JSON in attr — empty list */ }
        }
        return products;
      },
      socialHandle: dataset.photoSocialHandle,
      socialUrl: dataset.photoSocialUrl,
      travelCreator: dataset.photoTravelCreator === 'true',
    };
  }
}

if (!customElements.get('photo-gallery')) {
  customElements.define('photo-gallery', PhotoGallery);
}
